import FalconComponentCard from "components/common/FalconComponentCard";
import { Button, Col, Form, Spinner } from "react-bootstrap";
import { Pen, CheckCircleFill } from "react-bootstrap-icons";
import { useForm } from "react-hook-form";
import PermissionTable from "./PermissionTable";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import {
  EditPermissionRole,
  GetAllPermissionType,
  GetAllRoleType,
} from "../config/UserPermissionApi";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import paths from "routes/paths";
import BackButton from "components/common/custom/back-button";

const EditUserPermission = () => {
  const dispatch = useDispatch();
  const { allRolesType, allPermissionType } = useSelector(
    (state) => state.role
  );

  const navigate = useNavigate();
  const { userData } = useSelector((state) => state.auth);

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {},
  });

  const { id } = useParams();
  const [modules, setModules] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    dispatch(GetAllRoleType());
  }, [dispatch]);

  useEffect(() => {
    if (allPermissionType?.[0]?.syspri_name) {
      setValue("role_name", allPermissionType[0].syspri_name);
    }
  }, [allPermissionType, setValue]);

  useEffect(() => {
    dispatch(GetAllPermissionType(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (Array.isArray(allRolesType) && allPermissionType?.[0]?.rolemngmt_name) {
      const rolePermissions = allPermissionType[0].rolemngmt_name;

      const dynamicModules = allRolesType.map((module) => {
        const permissions = rolePermissions.find(
          (perm) => perm.rolemngmttyp_id === module.rolemngmttyp_id
        );

        const submodules = [];
        if (permissions) {
          Object.keys(permissions).forEach((key) => {
            if (
              !["rolemngmttyp_id", "rolemngmttyp_name"].includes(key) &&
              typeof permissions[key] === "number"
            ) {
              submodules.push({
                name: key.charAt(0).toUpperCase() + key.slice(1),
                key,
                value: permissions[key] === 1,
              });
            }
          });
        }

        return {
          id: module.rolemngmttyp_id,
          name: module.rolemngmttyp_name,
          submodules,
        };
      });

      setModules(dynamicModules);
    }
  }, [allRolesType, allPermissionType]);

  const handleGlobalPermissionChange = (value) => {
    setModules((prevModules) =>
      prevModules.map((mod) => ({
        ...mod,
        submodules: mod.submodules.map((sub) => ({
          ...sub,
          value,
        })),
      }))
    );
  };

  const handlePermissionChange = (moduleIndex, subIndex, value) => {
    setModules((prevModules) => {
      const updated = [...prevModules];
      updated[moduleIndex] = {
        ...updated[moduleIndex],
        submodules: updated[moduleIndex].submodules.map((sub, idx) =>
          idx === subIndex ? { ...sub, value } : sub
        ),
      };
      return updated;
    });
  };

  const handleMasterChange = (moduleIndex, value) => {
    setModules((prevModules) => {
      const updated = [...prevModules];
      updated[moduleIndex] = {
        ...updated[moduleIndex],
        submodules: updated[moduleIndex].submodules.map((sub) => ({
          ...sub,
          value,
        })),
      };
      return updated;
    });
  };

  const handleUpdatePermission = async () => {
    setIsSubmitting(true);
    setIsSaved(false);

    const rolemngmttyp = {};

    modules.forEach((module) => {
      rolemngmttyp[module.id] = {
        rolemngmttyp_id: module.id,
        ...module.submodules.reduce((acc, sub) => {
          acc[sub.key] = sub.value ? 1 : 0;
          return acc;
        }, {}),
      };
    });

    const payload = {
      syspri_id: parseInt(id),
      rolemngmttyp,
    };

    try {
      const response = await dispatch(EditPermissionRole(payload)).unwrap();

      if (response.success) {
        setIsSubmitting(false);
        setIsSaved(true);
        toast.success(response.message_en);

        // Reset state after a delay or navigate
        setTimeout(() => {
          setIsSaved(false);
          navigate(paths.userPermissionList);

        }, 1000);
        dispatch(GetAllPermissionType(id));


      } else {
        setIsSubmitting(false);
        toast.error(response.message_en);
      }
    } catch (error) {
      setIsSubmitting(false);
      toast.error("Failed to update permissions");
    }
  };

  return (
    <Form
      noValidate
      onSubmit={handleSubmit(handleUpdatePermission)}
      className="p-0"
    >
      <FalconComponentCard
        className="shadow-sm border-0"
        style={{ position: "relative" }}
      >
        <FalconComponentCard.Header
          className="d-flex fixed-header justify-content-between align-items-center"
          sx={{
            position: "sticky !important",
            top: 68,
            zIndex: 1020,
            boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
          }}
        >
          <BackButton
            path={paths.userPermissionList} h
            title="Update User Permission"
          />
          <Col xs="auto" className="d-flex gap-2">
            <Button
              size="sm"
              variant={isSaved ? "success" : "primary"}
              type="submit"
              disabled={isSubmitting || isSaved}
              className="px-2 fw-semibold shadow-none rounded-2 d-flex align-items-center gap-2 transition-base"
              style={{ transition: 'all 0.3s ease' }}
            >
              {isSubmitting ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                  <span>Updating...</span>
                </>
              ) : isSaved ? (
                <>
                  <CheckCircleFill size={16} />
                  <span>Updated</span>
                </>
              ) : (
                <>
                  <Pen size={14} />
                  <span className="text-white">Update</span>
                </>
              )}
            </Button>
          </Col>
        </FalconComponentCard.Header>
        <FalconComponentCard.Body
          className="p-3 p-md-4"
          style={{
            minHeight: "calc(100vh - 200px)",
          }}
        >
          <PermissionTable
            control={control}
            modules={modules}
            onGlobalPermissionChange={handleGlobalPermissionChange}
            onPermissionChange={handlePermissionChange}
            onMasterChange={handleMasterChange}
          />
        </FalconComponentCard.Body>
      </FalconComponentCard>
    </Form>
  );
};

export default EditUserPermission;
